// createStore — единственный держатель доменного состояния фронта (SPEC §4.1).
// Ядро: одна мутируемая StoreState + zustand-vanilla store для нотификации подписчиков (ревизия).
// attach(transport) крутит цикл for-await по transport.events(), маршрутизирует TransportEvent
// (§4.2) и прокачивает курсоры last-seq в transport.socket.setCursors на росте maxSeq (§4.4).
//
// Иммутабельность наружу обеспечивается селекторами: timeline/chatList мемоизируются по ревизии
// чата и отдают ссылочно-стабильные массивы (NFR-08) — пересборка только при правке этого чата.
import { createStore as createZustand } from 'zustand/vanilla';
import { initialState, ensureChatMessages } from './state.js';
import { resortChats } from './sort.js';
import { mergeServerEvent, reconcileOptimistic, setReactionOn, } from './merge.js';
import { projectSnapshot, restoreSnapshot } from './cache/snapshot.js';
import { createIndexedDbCache } from './cache/indexeddb.js';
const EMPTY_TIMELINE = Object.freeze([]);
const EMPTY_TYPING = Object.freeze([]);
const EMPTY_RECEIPTS = Object.freeze({ delivered: [], read: [] });
export function createStore(opts = {}) {
    const now = opts.now ?? (() => Date.now());
    const typingTtlMs = opts.typingTtlMs ?? 6000;
    const cacheWindowPerChat = opts.cacheWindowPerChat ?? 200;
    const cacheChatLimit = opts.cacheChatLimit ?? 40;
    const draftDebounceMs = opts.draftPersistDebounceMs ?? 800;
    const cacheDebounceMs = opts.cachePersistDebounceMs ?? 250;
    const historyPageSize = opts.historyPageSize ?? 50;
    // selfId мутируем: стор создаётся в App ДО логина (self ещё неизвестен), а затем прокидывается
    // через setSelf. Замыкания send/react/typing читают эту переменную, merge-функции — ctx.selfId.
    // Без этого selfId навсегда оставался undefined → своя оптимистичная реакция/«mine»/подавление
    // своего typing не работали (сервер не эхоит собственные reaction.set/typing автору).
    let selfId = opts.self?.id;
    const cache = opts.cache ?? lazyCache();
    const emit = (d) => opts.onDiagnostic?.(d);
    // Единственная мутируемая правда. zustand держит лишь ревизию для нотификации подписчиков.
    const state = initialState();
    const z = createZustand(() => ({ rev: 0 }));
    // Версии чатов для мемоизации селекторов (timeline/receipts/typing — ссылочная стабильность).
    const chatRev = new Map();
    const timelineCache = new Map();
    let transport = null;
    let abort = false;
    const typingTimers = new Map();
    const draftTimers = new Map();
    let cacheTimer = null;
    const ctx = { selfId, now, typingTtlMs, emit };
    // ── нотификация / ревизии ──────────────────────────────────────────────────
    function bump(...chats) {
        for (const c of chats)
            chatRev.set(c, (chatRev.get(c) ?? 0) + 1);
        z.setState((s) => ({ rev: s.rev + 1 }));
    }
    function bumpAll() {
        for (const c of Object.keys(state.chats.byId)) {
            chatRev.set(c, (chatRev.get(c) ?? 0) + 1);
        }
        z.setState((s) => ({ rev: s.rev + 1 }));
    }
    // ── курсоры last-seq наружу (SPEC §4.4) ─────────────────────────────────────
    let lastPushedCursors = '';
    function pushCursorsIfChanged() {
        if (!transport)
            return;
        const cursors = [];
        for (const [chatId, cm] of Object.entries(state.messages.byChat)) {
            cursors.push({ chatId: chatId, afterSeq: cm.maxSeq });
        }
        cursors.sort((a, b) => (a.chatId < b.chatId ? -1 : a.chatId > b.chatId ? 1 : 0));
        const key = JSON.stringify(cursors);
        if (key === lastPushedCursors)
            return;
        lastPushedCursors = key;
        transport.socket.setCursors(cursors);
        emit({ t: 'cursors.pushed', cursors });
    }
    // ── офлайн-кеш: дебаунс-персист (SPEC §4.6) ─────────────────────────────────
    function schedulePersist() {
        if (cacheTimer)
            clearTimeout(cacheTimer);
        cacheTimer = setTimeout(() => {
            cacheTimer = null;
            void persistNow();
        }, cacheDebounceMs);
    }
    async function persistNow() {
        const snap = projectSnapshot(state, { cacheWindowPerChat, cacheChatLimit }, now());
        try {
            await cache.save(snap);
        }
        catch (err) {
            emit({ t: 'action.failed', action: 'cache.save', message: String(err) });
        }
    }
    // ── typing auto-expire (SPEC §4.5.typing) ───────────────────────────────────
    function armTypingTimer(chatId, userId) {
        const key = `${chatId} ${userId}`;
        const existing = typingTimers.get(key);
        if (existing)
            clearTimeout(existing);
        const entry = state.typing[chatId]?.[userId];
        if (!entry)
            return;
        const delay = Math.max(0, entry.expiresAt - now());
        const timer = setTimeout(() => {
            typingTimers.delete(key);
            const perChat = state.typing[chatId];
            if (perChat && perChat[userId]) {
                delete perChat[userId];
                bump(chatId);
            }
        }, delay);
        typingTimers.set(key, timer);
    }
    // ── главный вход: подписка на транспорт (SPEC §4.2) ─────────────────────────
    function attach(t) {
        transport = t;
        abort = false;
        // курсоры из уже поднятого кеша (hydrate мог пройти раньше) — отдать сразу
        pushCursorsIfChanged();
        void runEventLoop(t);
    }
    async function runEventLoop(t) {
        try {
            for await (const ev of t.events()) {
                if (abort)
                    break;
                handleTransportEvent(ev);
            }
        }
        catch (err) {
            emit({ t: 'action.failed', action: 'events.loop', message: String(err) });
        }
    }
    function handleTransportEvent(ev) {
        switch (ev.t) {
            case 'server': {
                const before = maxSeqSnapshot();
                mergeServerEvent(state, ev.event, ctx);
                // typing-таймеры на typing-событие
                if (ev.event.t === 'typing' && (!selfId || ev.event.userId !== selfId)) {
                    armTypingTimer(ev.event.chatId, ev.event.userId);
                }
                notifyAfterServerEvent(ev.event.t === 'presence.update' ? undefined : affectedChat(ev));
                // курсоры — на росте maxSeq любого чата
                if (maxSeqChanged(before))
                    pushCursorsIfChanged();
                schedulePersist();
                return;
            }
            case 'conn': {
                state.conn.status = ev.status;
                // Успешный WS-open = сессия валидна (шлюз не пускает анонимные сокеты) → снимаем «нужен релогин».
                // Флаг мог взвестись на СТАРТЕ, когда WS-хендшейк шёл ДО логина и получал 4401; без сброса
                // модалка «Сессия истекла» висела поверх уже залогиненного приложения и не уходила после входа.
                if (ev.status === 'open')
                    state.conn.needRelogin = false;
                z.setState((s) => ({ rev: s.rev + 1 }));
                if (ev.status === 'open')
                    void onReconnectOpen();
                return;
            }
            case 'outbox.changed': {
                mirrorOutbox(ev.item.clientMsgId, ev.item.chatId, ev.item.status);
                return;
            }
            case 'auth.required': {
                state.conn.needRelogin = true;
                z.setState((s) => ({ rev: s.rev + 1 }));
                return;
            }
            case 'protocol.outdated': {
                state.conn.needReload = true;
                z.setState((s) => ({ rev: s.rev + 1 }));
                abort = true; // цикл прекращаем (SPEC §4.2)
                return;
            }
        }
    }
    function affectedChat(ev) {
        const e = ev.event;
        switch (e.t) {
            case 'message.new':
            case 'message.edited':
            case 'message.deleted':
            case 'reaction.set':
            case 'receipt.delivered':
            case 'receipt.read':
            case 'typing':
            case 'transcript.ready':
            case 'transcript.failed':
            case 'unread.update':
            case 'ack':
            case 'sync.delta':
                return e.chatId;
            case 'chat.updated':
                return e.chat.id;
            default:
                return undefined;
        }
    }
    function notifyAfterServerEvent(chatId) {
        if (chatId)
            bump(chatId);
        else
            bumpAll();
    }
    function maxSeqSnapshot() {
        const out = {};
        for (const [c, cm] of Object.entries(state.messages.byChat))
            out[c] = cm.maxSeq;
        return out;
    }
    function maxSeqChanged(before) {
        for (const [c, cm] of Object.entries(state.messages.byChat)) {
            if ((before[c] ?? -1) !== cm.maxSeq)
                return true;
        }
        return false;
    }
    // ── reconnect/cold-start примирение (SPEC §4.4 шаг 3 / §4.5.cold) ───────────
    async function onReconnectOpen() {
        if (!transport)
            return;
        pushCursorsIfChanged(); // курсоры держим актуальными — транспорт сам инициирует subscribe+sync
        try {
            const chats = await transport.rest.listChats();
            for (const chat of chats) {
                upsertChatFromRest(chat);
            }
            resortChats(state);
            bumpAll();
            schedulePersist();
        }
        catch (err) {
            emit({ t: 'action.failed', action: 'reconnect.listChats', message: String(err) });
        }
    }
    /** Примирить Chat из REST с локальным (myState/unread), не затирая «грязный» черновик. */
    function upsertChatFromRest(chat) {
        const dirty = state.draftDirty[chat.id] === true;
        const localDraft = state.drafts[chat.id];
        const merged = { ...chat };
        if (dirty && localDraft !== undefined) {
            merged.myState = { ...chat.myState, draft: localDraft };
        }
        else if (chat.myState.draft !== undefined) {
            state.drafts[chat.id] = chat.myState.draft;
        }
        state.chats.byId[chat.id] = merged;
        state.unread[chat.id] = merged.myState.unreadCount;
        for (const uid of merged.memberIds) {
            if (!state.users[uid])
                state.users[uid] = { id: uid, name: '' };
        }
    }
    // ── зеркалирование outbox.changed (SPEC §4.5.send шаг 4/5) ──────────────────
    function mirrorOutbox(clientMsgId, chatId, status) {
        const cm = state.messages.byChat[chatId];
        const opt = cm?.optimistic[clientMsgId];
        if (!opt)
            return; // уже сведено с committed — статус неактуален
        if (status === 'failed') {
            opt.localState = 'failed';
            emit({ t: 'optimistic.rolledback', clientMsgId, reason: 'outbox.failed' });
        }
        else if (status === 'sent' || status === 'queued') {
            opt.localState = status === 'sent' ? 'sent' : 'queued';
        }
        // 'delivered' без серверного Message не снимает оптимистичное — ждём message.new/ack по seq
        bump(chatId);
    }
    // ── hydrate (SPEC §4.6) ─────────────────────────────────────────────────────
    async function hydrate() {
        let snap = null;
        try {
            snap = await cache.load();
        }
        catch (err) {
            emit({ t: 'action.failed', action: 'cache.load', message: String(err) });
            return;
        }
        if (!snap)
            return;
        const cursors = restoreSnapshot(state, snap, ctx);
        bumpAll();
        // курсоры из снапшота — сразу транспорту (если уже attached), иначе при attach
        if (transport && cursors.length > 0)
            pushCursorsIfChanged();
    }
    // ── detach (SPEC §4.2) ──────────────────────────────────────────────────────
    function detach() {
        abort = true;
        for (const timer of typingTimers.values())
            clearTimeout(timer);
        typingTimers.clear();
        for (const timer of draftTimers.values())
            clearTimeout(timer);
        draftTimers.clear();
        if (cacheTimer) {
            clearTimeout(cacheTimer);
            cacheTimer = null;
        }
        // состояние и кеш НЕ сбрасываем (офлайн-чтение переживает detach/перезагрузку вкладки)
        transport = null;
    }
    // ─────────────────────────────────────────────────────────────────────────
    // СЕЛЕКТОРЫ (SPEC §4.8) — ссылочно-стабильные срезы
    // ─────────────────────────────────────────────────────────────────────────
    function buildTimeline(chatId) {
        const cm = state.messages.byChat[chatId];
        if (!cm)
            return EMPTY_TIMELINE;
        const out = [];
        for (const seq of cm.orderedSeq) {
            const m = cm.bySeq[seq];
            if (m)
                out.push(m);
        }
        for (const cid of cm.optimisticOrder) {
            const o = cm.optimistic[cid];
            if (o)
                out.push(o);
        }
        return out;
    }
    function timeline(chatId) {
        const rev = chatRev.get(chatId) ?? 0;
        const cached = timelineCache.get(chatId);
        if (cached && cached.rev === rev)
            return cached.value;
        const value = buildTimeline(chatId);
        timelineCache.set(chatId, { rev, value });
        return value;
    }
    const select = {
        chatList() {
            return state.chats.order.map((id) => state.chats.byId[id]).filter(Boolean);
        },
        chat(id) {
            return state.chats.byId[id];
        },
        timeline,
        message(chatId, seq) {
            return state.messages.byChat[chatId]?.bySeq[seq];
        },
        draft(id) {
            return state.drafts[id] ?? '';
        },
        unread(id) {
            return state.unread[id] ?? 0;
        },
        totalUnread() {
            if (state.serverTotalUnread !== undefined)
                return state.serverTotalUnread;
            let total = 0;
            for (const [id, count] of Object.entries(state.unread)) {
                const chat = state.chats.byId[id];
                if (chat?.myState.muted)
                    continue;
                total += count;
            }
            return total;
        },
        presence(userId) {
            return state.presence[userId];
        },
        typingIn(id) {
            const perChat = state.typing[id];
            if (!perChat)
                return EMPTY_TYPING;
            const t = now();
            const out = [];
            for (const [userId, entry] of Object.entries(perChat)) {
                if (entry.expiresAt > t)
                    out.push({ userId: userId, kind: entry.kind });
            }
            return out;
        },
        receiptsFor(chatId, seq) {
            const r = state.receipts[chatId];
            if (!r)
                return EMPTY_RECEIPTS;
            const delivered = [];
            const read = [];
            for (const [userId, upto] of Object.entries(r.delivered)) {
                if (upto >= seq)
                    delivered.push(userId);
            }
            for (const [userId, upto] of Object.entries(r.read)) {
                if (upto >= seq)
                    read.push(userId);
            }
            return { delivered, read };
        },
        connStatus() {
            return state.conn.status;
        },
        needRelogin() {
            return state.conn.needRelogin;
        },
        needReload() {
            return state.conn.needReload;
        },
    };
    // ─────────────────────────────────────────────────────────────────────────
    // ДЕЙСТВИЯ (SPEC §4.7) — оптимистичность с откатом
    // ─────────────────────────────────────────────────────────────────────────
    function requireTransport(action) {
        if (!transport)
            throw new Error(`client-store: действие "${action}" до attach(transport)`);
        return transport;
    }
    function send(chatId, draft) {
        const t = requireTransport('send');
        const clientMsgId = t.newClientMsgId();
        const dto = {
            clientMsgId,
            kind: draft.kind,
            text: draft.text,
            attachmentIds: draft.attachmentIds,
            voiceAttachmentId: draft.voiceAttachmentId,
            replyTo: draft.replyTo,
            mentions: draft.mentions,
        };
        const cm = ensureChatMessages(state, chatId);
        const optimistic = {
            clientMsgId,
            chatId,
            senderId: (selfId ?? ''),
            kind: draft.kind,
            text: draft.text,
            mentions: draft.mentions,
            createdAt: new Date(now()).toISOString(),
            localState: 'queued',
            optimistic: true,
        };
        cm.optimistic[clientMsgId] = optimistic;
        if (!cm.optimisticOrder.includes(clientMsgId))
            cm.optimisticOrder.push(clientMsgId);
        bump(chatId);
        // отдаём в персистентную очередь транспорта (офлайн не теряется — FR-PWA-04 на стороне транспорта)
        void t.outbox.enqueue(chatId, dto).catch((err) => {
            const opt = cm.optimistic[clientMsgId];
            if (opt)
                opt.localState = 'failed';
            emit({ t: 'action.failed', action: 'send.enqueue', message: String(err) });
            bump(chatId);
        });
        return clientMsgId;
    }
    async function retry(clientMsgId) {
        const t = requireTransport('retry');
        const loc = findOptimistic(clientMsgId);
        if (loc) {
            loc.opt.localState = 'queued';
            bump(loc.chatId);
        }
        await t.outbox.retry(clientMsgId);
    }
    async function discard(clientMsgId) {
        const t = requireTransport('discard');
        const loc = findOptimistic(clientMsgId);
        if (loc) {
            const cm = state.messages.byChat[loc.chatId];
            delete cm.optimistic[clientMsgId];
            const i = cm.optimisticOrder.indexOf(clientMsgId);
            if (i >= 0)
                cm.optimisticOrder.splice(i, 1);
            bump(loc.chatId);
        }
        await t.outbox.remove(clientMsgId);
    }
    function findOptimistic(clientMsgId) {
        for (const [chatId, cm] of Object.entries(state.messages.byChat)) {
            const opt = cm.optimistic[clientMsgId];
            if (opt)
                return { chatId: chatId, opt };
        }
        return null;
    }
    async function edit(messageId, text) {
        const t = requireTransport('edit');
        const idx = state.messageIndex[messageId];
        const cm = idx ? state.messages.byChat[idx.chatId] : undefined;
        const original = idx && cm ? cm.bySeq[idx.seq] : undefined;
        if (original && idx && cm) {
            // Иммутабельно: новая ссылка Message, иначе оптимистичная правка не перерисует ленту.
            cm.bySeq[idx.seq] = { ...original, text, editedAt: new Date(now()).toISOString() };
            bump(idx.chatId);
        }
        try {
            const updated = await t.rest.editMessage(messageId, text);
            if (idx) {
                applyServerMessage(idx.chatId, updated);
                bump(idx.chatId);
            }
        }
        catch (err) {
            if (original && idx && cm) {
                cm.bySeq[idx.seq] = original;
                bump(idx.chatId);
            }
            emit({ t: 'action.failed', action: 'edit', message: String(err) });
            throw err;
        }
    }
    async function remove(messageId, scope) {
        const t = requireTransport('remove');
        const idx = state.messageIndex[messageId];
        const cm = idx ? state.messages.byChat[idx.chatId] : undefined;
        const original = idx && cm ? cm.bySeq[idx.seq] : undefined;
        if (original && idx && cm) {
            // Иммутабельный тумбстоун (новая ссылка Message), иначе оптимистичное удаление не перерисует ленту.
            cm.bySeq[idx.seq] = {
                ...original,
                deletedAt: new Date(now()).toISOString(),
                text: undefined,
                attachments: undefined,
                voice: undefined,
                reactions: undefined,
            };
            bump(idx.chatId);
        }
        try {
            // DELETE по контракту api-server (R2 W7) возвращает {ok}, НЕ Message-тумбстоун (его несёт WS
            // message.deleted). Ответ НЕ применяем — иначе applyServerMessage сохранил бы {ok} как «сообщение»
            // с невалидными seq/createdAt (RangeError в форматтере даты). Оптимистичный тумбстоун уже стоит;
            // WS message.deleted сведётся идемпотентно.
            await t.rest.deleteMessage(messageId, scope);
            schedulePersist();
        }
        catch (err) {
            if (idx && cm && original) {
                cm.bySeq[idx.seq] = original;
                bump(idx.chatId);
            }
            emit({ t: 'action.failed', action: 'remove', message: String(err) });
            throw err;
        }
    }
    async function forward(messageId, toChatIds) {
        const t = requireTransport('forward');
        const clientMsgId = t.newClientMsgId();
        // оптимистичная вставка-болванка в каждый целевой чат (сведётся по clientMsgId ответных Message)
        for (const chatId of toChatIds) {
            const cm = ensureChatMessages(state, chatId);
            cm.optimistic[clientMsgId] = {
                clientMsgId,
                chatId,
                senderId: (selfId ?? ''),
                kind: 'text',
                createdAt: new Date(now()).toISOString(),
                localState: 'queued',
                optimistic: true,
            };
            if (!cm.optimisticOrder.includes(clientMsgId))
                cm.optimisticOrder.push(clientMsgId);
            bump(chatId);
        }
        try {
            const results = await t.rest.forwardMessage(messageId, { toChatIds, clientMsgId });
            for (const msg of results) {
                applyServerMessage(msg.chatId, msg);
                bump(msg.chatId);
            }
        }
        catch (err) {
            for (const chatId of toChatIds) {
                const cm = state.messages.byChat[chatId];
                if (cm?.optimistic[clientMsgId]) {
                    delete cm.optimistic[clientMsgId];
                    const i = cm.optimisticOrder.indexOf(clientMsgId);
                    if (i >= 0)
                        cm.optimisticOrder.splice(i, 1);
                    bump(chatId);
                }
            }
            emit({ t: 'action.failed', action: 'forward', message: String(err) });
            throw err;
        }
    }
    async function react(messageId, emoji, set) {
        const t = requireTransport('react');
        const idx = state.messageIndex[messageId];
        const cm = idx ? state.messages.byChat[idx.chatId] : undefined;
        const original = idx && cm ? cm.bySeq[idx.seq] : undefined;
        if (original && idx && cm && selfId) {
            // Иммутабельно: новая ссылка Message + свежий массив reactions, иначе timeline ссылочно не
            // меняется и лента не перерисовывается (пилюля реакции не появляется). Откат — вернуть original.
            const optimistic = {
                ...original,
                reactions: original.reactions?.map((r) => ({ ...r, userIds: r.userIds.slice() })),
            };
            setReactionOn(optimistic, emoji, selfId, set, selfId);
            cm.bySeq[idx.seq] = optimistic;
            bump(idx.chatId);
        }
        try {
            // REST-ответ реакций минимален (по контракту api-server R2 W7 сводку несёт WS reaction.set),
            // поэтому его НЕ применяем — иначе затёрли бы оптимистичную реакцию пустым массивом. Оставляем
            // оптимистичное состояние; серверное reaction.set (эхо участникам) сведётся идемпотентно.
            if (set)
                await t.rest.setReaction(messageId, emoji);
            else
                await t.rest.unsetReaction(messageId, emoji);
            schedulePersist();
        }
        catch (err) {
            if (idx && cm && original) {
                cm.bySeq[idx.seq] = original;
                bump(idx.chatId);
            }
            emit({ t: 'action.failed', action: 'react', message: String(err) });
            throw err;
        }
    }
    function markRead(chatId, seq) {
        const chat = state.chats.byId[chatId];
        if (!chat)
            return;
        if (seq > chat.myState.lastReadSeq) {
            chat.myState.lastReadSeq = seq; // монотонно
            recomputeUnreadLocal(chatId);
            bump(chatId);
        }
        // эфемерный WS-сигнал + durable-путь сервер обеспечит сам (SPEC §4.5.read)
        transport?.socket.send({ t: 'read', chatId, seq });
    }
    function recomputeUnreadLocal(chatId) {
        const chat = state.chats.byId[chatId];
        const cm = state.messages.byChat[chatId];
        if (!chat || !cm)
            return;
        let count = 0;
        for (const s of cm.orderedSeq) {
            if (s <= chat.myState.lastReadSeq)
                continue;
            const m = cm.bySeq[s];
            if (!m)
                continue;
            if (selfId && m.senderId === selfId)
                continue;
            count++;
        }
        state.unread[chatId] = count;
        chat.myState.unreadCount = count;
    }
    function setDraft(chatId, text) {
        state.drafts[chatId] = text;
        state.draftDirty[chatId] = true;
        const chat = state.chats.byId[chatId];
        if (chat)
            chat.myState.draft = text;
        bump(chatId);
        // дебаунс-персист на сервер → доезжает на другое устройство (FR-PWA-05)
        const existing = draftTimers.get(chatId);
        if (existing)
            clearTimeout(existing);
        const timer = setTimeout(() => {
            draftTimers.delete(chatId);
            state.draftDirty[chatId] = false; // ввод «устаканился» — серверное эхо можно применять
            if (transport) {
                void transport.rest.patchChatState(chatId, { draft: text }).catch((err) => {
                    emit({ t: 'action.failed', action: 'setDraft', message: String(err) });
                });
            }
        }, draftDebounceMs);
        draftTimers.set(chatId, timer);
    }
    function notifyTyping(chatId, kind) {
        transport?.socket.send({ t: 'typing', chatId, kind });
    }
    function openChat(chatId) {
        state.activeChats[chatId] = true;
    }
    function closeChat(chatId) {
        delete state.activeChats[chatId];
    }
    async function loadOlder(chatId) {
        const t = requireTransport('loadOlder');
        const cm = state.messages.byChat[chatId];
        const beforeSeq = cm && cm.orderedSeq.length > 0 ? cm.orderedSeq[0] : undefined;
        try {
            const older = await t.rest.history(chatId, { beforeSeq, limit: historyPageSize });
            const target = ensureChatMessages(state, chatId);
            for (const msg of older) {
                applyServerMessage(chatId, msg);
            }
            if (older.length < historyPageSize)
                target.gapBelow = false;
            bump(chatId);
            schedulePersist();
        }
        catch (err) {
            emit({ t: 'action.failed', action: 'loadOlder', message: String(err) });
            throw err;
        }
    }
    async function setPinned(chatId, v) {
        await patchChatStateOptimistic(chatId, 'pinned', v, { pinned: v });
    }
    async function setMuted(chatId, v) {
        await patchChatStateOptimistic(chatId, 'muted', v, { muted: v });
    }
    async function setArchived(chatId, v) {
        await patchChatStateOptimistic(chatId, 'archived', v, { archived: v });
    }
    async function patchChatStateOptimistic(chatId, field, value, patch) {
        const t = requireTransport(`set${field}`);
        const chat = state.chats.byId[chatId];
        const prev = chat ? chat.myState[field] : undefined;
        if (chat) {
            chat.myState[field] = value;
            resortChats(state);
            bumpAll();
        }
        try {
            const updated = await t.rest.patchChatState(chatId, patch);
            if (chat) {
                chat.myState = { ...chat.myState, ...updated };
                resortChats(state);
                bumpAll();
            }
        }
        catch (err) {
            if (chat && prev !== undefined) {
                chat.myState[field] = prev;
                resortChats(state);
                bumpAll();
            }
            emit({ t: 'action.failed', action: `set_${field}`, message: String(err) });
            throw err;
        }
    }
    /** Применить серверное Message к committed (через то же слияние, сведение по clientMsgId). */
    function applyServerMessage(chatId, msg) {
        const cm = ensureChatMessages(state, chatId);
        cm.bySeq[msg.seq] = msg;
        if (!cm.orderedSeq.includes(msg.seq)) {
            // вставка по возрастанию
            let lo = 0;
            let hi = cm.orderedSeq.length;
            while (lo < hi) {
                const mid = (lo + hi) >>> 1;
                if (cm.orderedSeq[mid] < msg.seq)
                    lo = mid + 1;
                else
                    hi = mid;
            }
            cm.orderedSeq.splice(lo, 0, msg.seq);
        }
        state.messageIndex[msg.id] = { chatId, seq: msg.seq };
        if (msg.seq > cm.maxSeq)
            cm.maxSeq = msg.seq;
        reconcileOptimistic(state, chatId, msg, ctx);
        const chat = state.chats.byId[chatId];
        if (chat && (!chat.lastMessage || msg.seq >= chat.lastMessage.seq)) {
            chat.lastMessage = {
                seq: msg.seq,
                senderId: msg.senderId,
                kind: msg.kind,
                preview: msg.deletedAt ? '' : msg.text ?? '',
                createdAt: msg.createdAt,
            };
        }
        pushCursorsIfChanged();
    }
    /**
     * Внести в стор чат, полученный из не-стор REST (директория: createDirect/createGroup §4.0). Сервер
     * не шлёт chat.created по WS, а openChat метаданные чата не тянет — без этого ConversationScreen висел
     * бы на «Загрузка…», а новый чат не появлялся бы в списке до реконнекта. Примиряет как listChats.
     */
    function ingestChat(chat) {
        upsertChatFromRest(chat);
        resortChats(state);
        bumpAll();
        schedulePersist();
    }
    const actions = {
        send,
        retry,
        discard,
        edit,
        remove,
        forward,
        react,
        markRead,
        setDraft,
        notifyTyping,
        openChat,
        closeChat,
        loadOlder,
        setPinned,
        setMuted,
        setArchived,
        ingestChat,
    };
    function subscribe(listener) {
        return z.subscribe(listener);
    }
    /** Прокинуть текущего пользователя после логина/me (стор создаётся ДО того, как self известен). */
    function setSelf(self) {
        selfId = self?.id;
        ctx.selfId = self?.id;
    }
    return { attach, detach, hydrate, select, actions, subscribe, setSelf };
}
// ── вспомогательное ──────────────────────────────────────────────────────────
function deepCloneReactions(list) {
    return list.map((r) => ({ ...r, userIds: r.userIds.slice() }));
}
/** Ленивая обёртка над IndexedDB-кешем: создаётся при первом обращении (SSR-safe). */
function lazyCache() {
    let inner = null;
    function get() {
        if (!inner)
            inner = createIndexedDbCache();
        return inner;
    }
    return {
        load: () => get().load(),
        save: (s) => get().save(s),
        clear: () => get().clear(),
    };
}
//# sourceMappingURL=store.js.map